import React, { useEffect, useState, useRef } from "react";
import { api, BRL } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Trash2, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SIZE_PRESETS = { pizza: ["Pequena", "Grande", "GG"], pastel: ["P", "G"] };
const sizeNamesFor = (cat) => SIZE_PRESETS[cat] || [];
const ALL_SIZE_KEYS = ["Pequena", "Grande", "GG", "P", "G"];
const empty = { name: "", description: "", price: "", category: "", image: "", available: true };

export default function ProductManager() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    api.get("/products").then(({ data }) => setProducts(data));
    api.get("/categories").then(({ data }) => setCategories(data));
  };
  useEffect(load, []);

  const catName = (slug) => categories.find((c) => c.slug === slug)?.name || slug;

  const openNew = () => {
    setEditing(null);
    setForm(empty);
    setOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    const sz = {};
    ALL_SIZE_KEYS.forEach((n) => (sz[`sz${n}`] = ""));
    (p.sizes || []).forEach((s) => {
      sz[`sz${s.name}`] = String(s.price);
    });
    setForm({ ...p, price: String(p.price), ...sz });
    setOpen(true);
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = data.url.startsWith("http") ? data.url : `${BACKEND_URL}${data.url}`;
      setForm((f) => ({ ...f, image: url }));
      toast.success("Foto enviada!");
    } catch {
      toast.error("Falha ao enviar a foto.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    if (!form.name || !form.price || !form.category) {
      toast.error("Preencha nome, preço e categoria.");
      return;
    }
    setSaving(true);
    const num = (v) => parseFloat(String(v).replace(",", ".")) || 0;
    let sizes = null;
    const presetNames = sizeNamesFor(form.category);
    if (presetNames.length) {
      const built = presetNames
        .map((n) => ({ name: n, price: num(form[`sz${n}`]) }))
        .filter((s) => s.price > 0);
      if (built.length) sizes = built;
    }
    const payload = {
      name: form.name,
      description: form.description || "",
      price: num(form.price),
      category: form.category,
      image: form.image || "",
      available: form.available !== false,
      sizes,
    };
    try {
      if (editing) await api.put(`/admin/products/${editing.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(editing ? "Produto atualizado!" : "Produto adicionado!");
      setOpen(false);
      load();
    } catch {
      toast.error("Erro ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  const del = async (p) => {
    if (!window.confirm(`Remover "${p.name}"?`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast("Produto removido");
      load();
    } catch {
      toast.error("Erro ao remover.");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-display font-black text-2xl tracking-tight">Cardápio</h2>
          <p className="text-sm text-stone-400">Edite nomes, descrições, preços e fotos.</p>
        </div>
        <button
          onClick={openNew}
          data-testid="new-product-btn"
          className="inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold hover:bg-primary/90 transition-colors"
        >
          <Plus className="w-4 h-4" /> Novo produto
        </button>
      </div>

      <div className="grid gap-3 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
        {products.map((p) => (
          <div
            key={p.id}
            data-testid={`manage-product-${p.id}`}
            className="bg-[#1C1917] border border-[#292524] rounded-xl overflow-hidden flex"
          >
            <img src={p.image} alt={p.name} className="w-24 h-24 object-cover" />
            <div className="flex-1 p-3 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold truncate">{p.name}</p>
                  <p className="text-xs text-stone-500">{catName(p.category)}</p>
                </div>
                {p.available === false && (
                  <span className="text-[10px] bg-destructive/20 text-destructive px-1.5 py-0.5 rounded-full whitespace-nowrap">Indisponível</span>
                )}
              </div>
              <p className="text-secondary font-bold text-sm mt-1">{BRL(p.price)}</p>
              <div className="flex gap-2 mt-2">
                <button
                  onClick={() => openEdit(p)}
                  data-testid={`edit-product-${p.id}`}
                  className="inline-flex items-center gap-1 text-xs rounded-full border border-[#292524] px-2.5 py-1 hover:bg-[#292524] transition-colors"
                >
                  <Pencil className="w-3 h-3" /> Editar
                </button>
                <button
                  onClick={() => del(p)}
                  data-testid={`delete-product-${p.id}`}
                  className="inline-flex items-center gap-1 text-xs rounded-full border border-[#292524] px-2.5 py-1 text-destructive hover:bg-destructive/10 transition-colors"
                >
                  <Trash2 className="w-3 h-3" /> Remover
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#1C1917] border-[#292524] text-stone-100 max-h-[90vh] overflow-y-auto" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-xl">
              {editing ? "Editar produto" : "Novo produto"}
            </DialogTitle>
            <DialogDescription className="text-stone-400">
              Preencha os dados e envie uma foto do produto.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-24 h-24 rounded-lg bg-[#0C0A09] border border-[#292524] overflow-hidden grid place-items-center shrink-0">
                {form.image ? (
                  <img src={form.image} alt="preview" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-xs text-stone-600">Sem foto</span>
                )}
              </div>
              <div>
                <input ref={fileRef} type="file" accept="image/*" onChange={onUpload} className="hidden" data-testid="product-image-input" />
                <button
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  data-testid="upload-image-btn"
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#292524] px-3 py-2 text-sm hover:bg-[#292524] transition-colors disabled:opacity-60"
                >
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {uploading ? "Enviando..." : "Enviar foto"}
                </button>
                <p className="text-xs text-stone-500 mt-1">JPG, PNG ou WEBP</p>
              </div>
            </div>

            <div>
              <Label className="text-stone-300">Nome</Label>
              <Input data-testid="product-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0C0A09] border-[#292524] text-stone-100" />
            </div>
            <div>
              <Label className="text-stone-300">Descrição</Label>
              <Textarea data-testid="product-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-[#0C0A09] border-[#292524] text-stone-100" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-stone-300">Preço (R$)</Label>
                <Input data-testid="product-price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="0,00" className="bg-[#0C0A09] border-[#292524] text-stone-100" />
              </div>
              <div>
                <Label className="text-stone-300">Categoria</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="product-category" className="bg-[#0C0A09] border-[#292524] text-stone-100">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-stone-300">Disponível para venda</Label>
              <Switch checked={form.available !== false} onCheckedChange={(v) => setForm({ ...form, available: v })} data-testid="product-available" />
            </div>

            {sizeNamesFor(form.category).length > 0 && (
              <div className="rounded-xl border border-[#292524] p-3">
                <p className="text-sm font-semibold mb-1">Tamanhos (preços por tamanho)</p>
                <p className="text-xs text-stone-500 mb-3">Preencha os tamanhos vendidos. Deixe em branco para não oferecer aquele tamanho.</p>
                <div className={`grid gap-2 ${sizeNamesFor(form.category).length === 2 ? "grid-cols-2" : "grid-cols-3"}`}>
                  {sizeNamesFor(form.category).map((n) => (
                    <div key={n}>
                      <Label className="text-stone-300 text-xs">{n} (R$)</Label>
                      <Input
                        data-testid={`product-size-${n}`}
                        value={form[`sz${n}`] ?? ""}
                        onChange={(e) => setForm({ ...form, [`sz${n}`]: e.target.value })}
                        placeholder="0,00"
                        className="bg-[#0C0A09] border-[#292524] text-stone-100"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <button
              onClick={save}
              disabled={saving}
              data-testid="save-product-btn"
              className="w-full rounded-full bg-primary text-primary-foreground py-3 font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60"
            >
              {saving ? "Salvando..." : "Salvar"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
